import { Subject } from 'rxjs';

/**
 * @module eventService
 * @description
 * This module implements a system-wide event bus using RxJS Subject.
 * It allows for decoupled communication between different packages and modules within the application.
 * Services can publish events when significant actions occur (e.g., data creation/update),
 * and other services or UI components can subscribe to these events to react accordingly.
 * This promotes a more modular and maintainable architecture by reducing direct dependencies.
 */

/**
 * @private
 * @type {Subject<Object>}
 * @description
 * The core RxJS Subject that acts as the event broadcaster.
 * All events are pushed onto this subject and it multicasts them to all registered subscribers.
 * The object emitted will have a 'type' (from EVENT_TYPES) and a 'payload' (event-specific data).
 */
const eventSubject = new Subject();

/**
 * @readonly
 * @enum {string}
 * @description
 * A dictionary of constants representing the unique types of events that can be published
 * and subscribed to across the application. 
 * It is crucial to use these defined types to ensure consistency and prevent typos.
 * When adding new types of events that need to be communicated across the system,
 * they should be defined here.
 * 
 * NAMING CONVENTIONS:
 * - Use SCREAMING_SNAKE_CASE for all event names
 * - Start with the entity/domain name (e.g., ACCOMMODATION_, PLACE_, USER_)
 * - Use past tense verbs (e.g., ADDED, UPDATED, DELETED, LINKED)
 * - Be specific about the action (avoid generic terms like ACTIVITY_ADDED)
 * - Include context when needed (e.g., PLACE_ORPHANED vs PLACE_DELETED)
 * 
 * PAYLOAD REQUIREMENTS:
 * - All events must include: { userId, tripId, timestamp }
 * - Entity events must include: { id, ...entityData }
 * - Relationship events must include relevant entity IDs
 * 
 * Example event types:
 * - ACCOMMODATION_ADDED: Published when a new accommodation is created
 * - PLACE_ORPHANED: Published when a place has no remaining references
 * - USER_RESPONDED_TO_SUGGESTION: Published when user acts on a suggestion
 */
export const EVENT_TYPES = {
  // =====================================================
  // ACTIVE EVENTS - Currently implemented and used
  // =====================================================
  
  // Accommodation Events (ACTIVE)
  ACCOMMODATION_ADDED: 'accommodation_added', // ✅ ACTIVE: Published by accommodationService.createAccommodation()
  ACCOMMODATION_CITY_CHANGED: 'accommodation_city_changed', // ✅ ACTIVE: Published by accommodationService.updateAccommodation()
  ACCOMMODATION_DELETED: 'accommodation_deleted', // ✅ ACTIVE: Published by accommodationService.deleteAccommodation()
  ACCOMMODATION_PLACE_LINKED: 'accommodation_place_linked', // ✅ ACTIVE: Published when accommodation is linked to a place
  ACCOMMODATION_PLACE_UNLINKED: 'accommodation_place_unlinked', // ✅ ACTIVE: Published when accommodation is unlinked from a place
  
  // Accommodation-Day Integration Events (ACTIVE - Phase 2A)
  ACCOMMODATION_DAY_LINK_CREATED: 'accommodation_day_link_created', // ✅ ACTIVE: Published when accommodation is linked to a day
  ACCOMMODATION_DAY_LINK_REMOVED: 'accommodation_day_link_removed', // ✅ ACTIVE: Published when accommodation-day link is removed
  DAY_ACCOMMODATION_ADDED: 'day_accommodation_added', // ✅ ACTIVE: Published when accommodation content is added to a day
  DAY_ACCOMMODATION_REMOVED: 'day_accommodation_removed', // ✅ ACTIVE: Published when accommodation content is removed from a day
  ACCOMMODATION_UPDATED: 'accommodation_updated', // ✅ ACTIVE: Published when accommodation is modified (general updates)
  
  // Content Block Events (ACTIVE - Phase 2B)
  DAY_CONTENT_BLOCK_ADDED: 'day_content_block_added', // ✅ ACTIVE: Published when a content block is added to a day
  DAY_CONTENT_BLOCK_REMOVED: 'day_content_block_removed', // ✅ ACTIVE: Published when a content block is removed from a day
  DAY_CONTENT_BLOCK_UPDATED: 'day_content_block_updated', // ✅ ACTIVE: Published when a content block is updated
  ACCOMMODATION_CONTENT_BLOCKS_UPDATED: 'accommodation_content_blocks_updated', // ✅ ACTIVE: Published when accommodation content block metadata is updated
  
  // Place Events (ACTIVE)
  PLACE_ADDED: 'place_added', // ✅ ACTIVE: Published when a new place is created
  PLACE_UPDATED: 'place_updated', // ✅ ACTIVE: Published when place data is modified
  PLACE_DELETED: 'place_deleted', // ✅ ACTIVE: Published when a place is removed
  PLACE_ORPHANED: 'place_orphaned', // ✅ ACTIVE: Published when a place has no remaining references
  PLACE_USAGE_ANALYZED: 'place_usage_analyzed', // ✅ ACTIVE: Published when place usage analysis is completed

  // Day Events (ACTIVE)
  DAY_ADDED: 'day_added', // ✅ ACTIVE: Published when a new day is created in the itinerary
  DAY_UPDATED: 'day_updated', // ✅ ACTIVE: Published when day data is modified
  DAY_DELETED: 'day_deleted', // ✅ ACTIVE: Published when a day is removed from the itinerary

  // Travel Events (ACTIVE)
  TRAVEL_ADDED: 'travel_added', // ✅ ACTIVE: Published when a new travel item is added
  TRAVEL_UPDATED: 'travel_updated', // ✅ ACTIVE: Published when travel data is modified
  TRAVEL_DELETED: 'travel_deleted', // ✅ ACTIVE: Published when a travel item is removed

  // Document Processing Events (ACTIVE)
  DOCUMENT_ADDED: 'document_added', // ✅ ACTIVE: Published when a new document is created/added
  DOCUMENT_UPLOADED: 'document_uploaded', // ✅ ACTIVE: Published when document is uploaded
  DOCUMENT_PROCESSED: 'document_processed', // ✅ ACTIVE: Published by documentProcessingService with extracted data
  DOCUMENT_PROCESSING_FAILED: 'document_processing_failed', // ✅ ACTIVE: Published when document processing fails
  DOCUMENT_UPDATED: 'document_updated', // ✅ ACTIVE: Published when document metadata is updated
  DOCUMENT_DELETED: 'document_deleted', // ✅ ACTIVE: Published when document is deleted
  DOCUMENT_ASSOCIATED: 'document_associated', // ✅ ACTIVE: Published when document is associated with an entity
  DOCUMENT_DISASSOCIATED: 'document_disassociated', // ✅ ACTIVE: Published when document is disassociated from an entity
  
  // Suggestion System Events (ACTIVE)
  PLACE_SUGGESTION: 'place_suggestion', // ✅ ACTIVE: Published by placesSuggestionService (legacy system)
  SUGGESTION_CREATED: 'suggestion_created', // ✅ ACTIVE: Published by placesSuggestionService (legacy system)
  SUGGESTION_ACCEPTED: 'suggestion_accepted',
  SUGGESTION_REJECTED: 'suggestion_rejected', // ✅ ACTIVE: Published by PlaceSuggestionPopup when user rejects
  SHOW_IMMEDIATE_SUGGESTION_POPUP: 'show_immediate_suggestion_popup', // ✅ ACTIVE: Published by SuggestionService to trigger UI popup
  USER_RESPONDED_TO_SUGGESTION: 'user_responded_to_suggestion', // ✅ ACTIVE: Published by UI to notify SuggestionService of user choice
  LINK_ACCOMMODATION_TO_PLACE: 'link_accommodation_to_place', // ✅ ACTIVE: Published by SuggestionService to AccommodationService
  PENDING_SUGGESTIONS_UPDATED: 'pending_suggestions_updated', // ✅ ACTIVE: Published by SuggestionService to notify UI of list changes
  SUGGESTION_PROCESSED: 'suggestion_processed', // ✅ ACTIVE: Published by SuggestionService to notify of final suggestion status
  PLACE_DISAMBIGUATION_REQUIRED: 'place_disambiguation_required', // ✅ ACTIVE: Published when multiple similar places are found for accommodation
  
  // =====================================================
  // PLACEHOLDER EVENTS - Defined for future features
  // =====================================================
  
  // Travel Events (PLACEHOLDER - for future travel module expansion)
  FLIGHT_ADDED: 'flight_added', // 🔄 PLACEHOLDER: For flight booking functionality (travel package)
  TRAIN_ADDED: 'train_added', // 🔄 PLACEHOLDER: For train booking functionality
  CAR_RENTAL_ADDED: 'car_rental_added', // 🔄 PLACEHOLDER: For car rental functionality
  BUS_ADDED: 'bus_added', // 🔄 PLACEHOLDER: For bus booking functionality
  SHIP_ADDED: 'ship_added', // 🔄 PLACEHOLDER: For ferry/cruise booking functionality
  
  // Daily Itinerary Events (PLACEHOLDER - for days package expansion)
  DAY_ACTIVITY_ADDED: 'day_activity_added', // 🔄 PLACEHOLDER: Replaces generic ACTIVITY_ADDED - for daily itinerary activities
  DAY_NOTE_UPDATED: 'day_note_updated', // 🔄 PLACEHOLDER: For day summary/notes updates
  DAY_REORDERED: 'day_reordered', // 🔄 PLACEHOLDER: For day sequence changes
  
  // User/Authentication Events (PLACEHOLDER - for auth expansion)
  USER_LOGIN: 'user_login', // 🔄 PLACEHOLDER: For user authentication tracking
  USER_LOGOUT: 'user_logout', // 🔄 PLACEHOLDER: For user session management
  USER_REGISTERED: 'user_registered', // 🔄 PLACEHOLDER: For new user registration
  USER_PROFILE_UPDATED: 'user_profile_updated', // 🔄 PLACEHOLDER: For user profile changes
  
  // Trip Management Events
  TRIP_CREATED: 'trip_created', // 🔄 PLACEHOLDER: For trip creation tracking
  TRIP_UPDATED: 'trip_updated', // ✅ ACTIVE: Published when trip data changes (e.g., dates updated)
  TRIP_DELETED: 'trip_deleted', // ✅ ACTIVE: Published by firestoreService.deleteTrip() with deletion statistics
  TRIP_SHARED: 'trip_shared', // 🔄 PLACEHOLDER: For trip sharing functionality
  TRIP_MEMBER_ADDED: 'trip_member_added', // 🔄 PLACEHOLDER: For collaborative trip planning features
  TRIP_MEMBER_REMOVED: 'trip_member_removed', // 🔄 PLACEHOLDER: For collaborative trip planning features
  TRIP_MEMBER_PERMISSIONS_CHANGED: 'trip_member_permissions_changed', // 🔄 PLACEHOLDER: For collaboration permission management
  
  // AI/ML Service Events (PLACEHOLDER - for future AI integration)
  AI_DOCUMENT_ANALYSIS_REQUESTED: 'ai_document_analysis_requested', // 🔄 PLACEHOLDER: Improved naming for AI document processing
  AI_DOCUMENT_ANALYSIS_COMPLETED: 'ai_document_analysis_completed', // 🔄 PLACEHOLDER: Improved naming for AI document processing
  AI_DOCUMENT_ANALYSIS_FAILED: 'ai_document_analysis_failed', // 🔄 PLACEHOLDER: Improved naming for AI document processing
  AI_TRIP_SUGGESTIONS_GENERATED: 'ai_trip_suggestions_generated', // 🔄 PLACEHOLDER: For AI-powered trip recommendations
  AI_ITINERARY_OPTIMIZED: 'ai_itinerary_optimized', // 🔄 PLACEHOLDER: For AI-powered itinerary optimization
  
  // Notification Events (PLACEHOLDER - for notification system)
  NOTIFICATION_CREATED: 'notification_created', // 🔄 PLACEHOLDER: For in-app notifications
  NOTIFICATION_READ: 'notification_read', // 🔄 PLACEHOLDER: For notification state management
  EMAIL_SENT: 'email_sent', // 🔄 PLACEHOLDER: For email notification tracking
  PUSH_NOTIFICATION_SENT: 'push_notification_sent', // 🔄 PLACEHOLDER: For push notification tracking
  
  // Cleanup Events (ACTIVE)
  ACCOMMODATION_DAYS_CLEANUP_COMPLETED: 'accommodation_days_cleanup_completed', // ✅ ACTIVE: Published when accommodation deletion triggers day cleanup
  
  // Entity Linking Events (ACTIVE)
  ENTITY_LINKED: 'entity_linked', // ✅ ACTIVE: Published when any entity link is created
  ENTITY_UNLINKED: 'entity_unlinked', // ✅ ACTIVE: Published when any entity link is removed

  // Connection Registry Events (ACTIVE - Phase 1)
  LINK_CREATED: 'link_created',
  LINK_UPDATED: 'link_updated',
  LINK_REMOVED: 'link_removed',
  ENTITY_LINKS_PURGED: 'entity_links_purged',
  LINK_REGISTRY_EXTRACT_COMPLETED: 'link_registry_extract_completed',
  
  // Analytics Events (PLACEHOLDER - for usage tracking)
  USER_ACTION_TRACKED: 'user_action_tracked', // 🔄 PLACEHOLDER: For user behavior analytics
  FEATURE_USAGE_LOGGED: 'feature_usage_logged', // 🔄 PLACEHOLDER: For feature adoption tracking
  ERROR_REPORTED: 'error_reported', // 🔄 PLACEHOLDER: For error tracking and monitoring
  PERFORMANCE_METRIC_RECORDED: 'performance_metric_recorded', // 🔄 PLACEHOLDER: For performance monitoring
  
  // Budget/Finance Events (PLACEHOLDER - for extras package)
  BUDGET_CREATED: 'budget_created', // 🔄 PLACEHOLDER: For budget management functionality
  EXPENSE_ADDED: 'expense_added', // 🔄 PLACEHOLDER: For expense tracking
  CURRENCY_CONVERTED: 'currency_converted', // 🔄 PLACEHOLDER: For currency conversion tracking
  PAYMENT_PROCESSED: 'payment_processed', // 🔄 PLACEHOLDER: For payment integration
  
  // Calendar Integration Events (PLACEHOLDER - for calendar sync)
  CALENDAR_EVENT_CREATED: 'calendar_event_created', // 🔄 PLACEHOLDER: For external calendar integration
  CALENDAR_SYNC_COMPLETED: 'calendar_sync_completed', // 🔄 PLACEHOLDER: For calendar synchronization
  CALENDAR_SYNC_FAILED: 'calendar_sync_failed' // 🔄 PLACEHOLDER: For calendar sync error handling
};

/**
 * @service eventBridgeService
 * @description
 * Provides the public API for interacting with the event bus.
 */
export const eventBridgeService = {
  /**
   * Publishes an event to all active subscribers.
   * 
   * @param {string} type - The type of event to publish. Must be one of the keys from EVENT_TYPES.
   * @param {Object} payload - The data associated with the event. This can be any JavaScript object
   *                           containing information relevant to the event (e.g., the created entity, 
   *                           extracted document data, etc.).
   * @returns {void}
   * 
   * @example
   * // In accommodationService.js, after creating an accommodation:
   * // const newAccommodation = { id: '123', name: 'Hotel Paradise', ... };
   * // eventBridgeService.publish(EVENT_TYPES.ACCOMMODATION_ADDED, newAccommodation);
   */
  publish(type, payload) {
    console.log('📡 [EventBridge] publish: Publishing event', {
      eventType: type,
      hasPayload: !!payload,
      payloadKeys: payload ? Object.keys(payload) : [],
      timestamp: new Date().toISOString(),
      payloadPreview: payload ? {
        userId: payload.userId,
        tripId: payload.tripId,
        id: payload.id || payload.dayId || payload.accommodationId || payload.contentBlockId,
        type: payload.type
      } : null
    });
    
    eventSubject.next({ type, payload });
    
    console.log('✅ [EventBridge] publish: Event published successfully', {
      eventType: type
    });
  },
  
  /**
   * Subscribes to a specific type of event.
   * 
   * @param {string} eventType - The type of event to listen for (must be one of EVENT_TYPES).
   * @param {function(Object): void} handler - A callback function that will be executed when an event
   *                                       of the specified type is published. The handler will receive
   *                                       the event's payload as its only argument.
   * @returns {import('rxjs').Subscription} An RxJS Subscription object. The caller should store this
   *                                   subscription and call its `unsubscribe()` method when the listener
   *                                   is no longer needed (e.g., in a React component's cleanup phase)
   *                                   to prevent memory leaks.
   * @example
   * // In suggestionService.js, to listen for new accommodations:
   * // const accommodationSubscription = eventBridgeService.subscribe(
   * //   EVENT_TYPES.ACCOMMODATION_ADDED, 
   * //   (accommodationData) => {
   * //     console.log('New accommodation added:', accommodationData);
   * //     // ...logic to generate suggestions...
   * //   }
   * // );
   * // Later, to cleanup: accommodationSubscription.unsubscribe();
   */
  subscribe(eventType, handler) {
    console.log('👂 [EventBridge] subscribe: Setting up subscription', {
      eventType,
      handlerName: handler.name || 'anonymous',
      timestamp: new Date().toISOString()
    });
    
    return eventSubject
      .pipe(
        // filter(event => event.type === eventType) // Alternative way to filter using RxJS operators
      )
      .subscribe(event => {
        if (event.type === eventType) {
          console.log('🎯 [EventBridge] subscribe: Event received for handler', {
            eventType,
            handlerName: handler.name || 'anonymous',
            hasPayload: !!event.payload,
            payloadPreview: event.payload ? {
              userId: event.payload.userId,
              tripId: event.payload.tripId,
              id: event.payload.id || event.payload.dayId || event.payload.accommodationId || event.payload.contentBlockId
            } : null
          });
          
          try {
            handler(event.payload);
            console.log('✅ [EventBridge] subscribe: Handler executed successfully', {
              eventType,
              handlerName: handler.name || 'anonymous'
            });
          } catch (error) {
            console.error('❌ [EventBridge] subscribe: Handler execution failed', {
              eventType,
              handlerName: handler.name || 'anonymous',
              error: error.message,
              stack: error.stack
            });
          }
        }
      });
  },
  
  /**
   * Subscribes to multiple event types with a single handler.
   * 
   * @param {string[]} eventTypes - An array of event types (from EVENT_TYPES) to listen for.
   * @param {function(string, Object): void} handler - A callback function that will be executed when any of
   *                                                the specified event types are published. The handler
   *                                                will receive the event type (string) as its first
   *                                                argument and the event's payload (Object) as its second.
   * @returns {import('rxjs').Subscription} An RxJS Subscription object. Similar to `subscribe`,
   *                                   this should be unsubscribed when no longer needed.
   * @example
   * // const multiEventSubscription = eventBridgeService.subscribeToMany(
   * //   [EVENT_TYPES.FLIGHT_ADDED, EVENT_TYPES.ACCOMMODATION_ADDED],
   * //   (eventType, data) => {
   * //     if (eventType === EVENT_TYPES.FLIGHT_ADDED) {
   * //       console.log('Flight added:', data);
   * //     } else {
   * //       console.log('Accommodation added:', data);
   * //     }
   * //   }
   * // );
   * // Later, to cleanup: multiEventSubscription.unsubscribe();
   */
  subscribeToMany(eventTypes, handler) {
    return eventSubject
      .pipe(
        // filter(event => eventTypes.includes(event.type)) // Alternative way to filter
      )
      .subscribe(event => {
        if (eventTypes.includes(event.type)) {
          handler(event.type, event.payload);
        }
      });
  }
}; 